import React, { useState } from 'react';
import { useParams } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Trash2, UserPlus, Crown, Shield, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ProjectUser {
  id: string;
  user_id: number;
  role: 'super_admin' | 'manager' | 'ae';
  username: string;
  email: string;
  created_at: string;
}

export default function ProjectUserManagement() {
  const { id: projectId } = useParams<{ id: string }>();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'ae' | 'manager'>('ae');
  const [currentUser] = useState({ project_role: 'super_admin', id: 1 });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch project users
  const { data: projectUsers = [], isLoading } = useQuery({
    queryKey: [`/api/project/${projectId}/users`],
    enabled: !!projectId,
  });

  // Invite user mutation
  const inviteUserMutation = useMutation({
    mutationFn: async (data: { email: string; role: string }) => {
      const response = await fetch(`/api/project/${projectId}/invite`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' },
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/project/${projectId}/users`] });
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('ae');
      toast({
        title: "User invited successfully",
        description: "The user has been added to the project.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to invite user",
        description: error.message || "An error occurred while inviting the user.",
        variant: "destructive",
      });
    },
  });

  // Update user role mutation
  const updateUserRoleMutation = useMutation({
    mutationFn: async (data: { userId: number; role: string }) => {
      return await apiRequest(`/api/project/${projectId}/user/${data.userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: data.role }),
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/project/${projectId}/users`] });
      toast({
        title: "Role updated successfully",
        description: "The user's role has been changed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update role",
        description: error.message || "An error occurred while updating the role.",
        variant: "destructive",
      });
    },
  });

  // Remove user mutation
  const removeUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      return await apiRequest(`/api/project/${projectId}/user/${userId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/project/${projectId}/users`] });
      toast({
        title: "User removed successfully",
        description: "The user has been removed from the project.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to remove user",
        description: error.message || "An error occurred while removing the user.",
        variant: "destructive",
      });
    },
  });

  const handleInviteUser = () => {
    if (!inviteEmail.trim()) {
      toast({
        title: "Email required",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    inviteUserMutation.mutate({ email: inviteEmail, role: inviteRole });
  };

  const handleRoleChange = (userId: number, newRole: string) => {
    updateUserRoleMutation.mutate({ userId, role: newRole });
  };

  const handleRemoveUser = (userId: number) => {
    removeUserMutation.mutate(userId);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'manager':
        return <Shield className="h-4 w-4 text-blue-500" />;
      case 'ae':
        return <User className="h-4 w-4 text-green-500" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'default';
      case 'manager':
        return 'secondary';
      case 'ae':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const canModifyUser = (user: ProjectUser) => {
    if (currentUser.project_role === 'super_admin') return true;
    if (currentUser.project_role === 'manager' && user.role !== 'super_admin') return true;
    return false;
  };

  const isCurrentUser = (user: ProjectUser) => {
    return user.user_id === currentUser.id;
  };

  // Access control - only super_admin and manager can access this page
  if (currentUser.project_role === 'ae') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
              <p className="text-gray-600 dark:text-gray-400">
                You don't have permission to access this page. Only Super Admins and Managers can manage project users.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading project users...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Project User Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage users assigned to this project. Control access and assign roles.
        </p>
      </div>

      {/* Assigned Users Section */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Assigned Users</CardTitle>
              <CardDescription>
                Users currently assigned to this project and their roles
              </CardDescription>
            </div>
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#4AE2C9] hover:bg-[#3BC9B3] text-black">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite New User</DialogTitle>
                  <DialogDescription>
                    Add a new user to this project. They will receive access based on the role you assign.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="user@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select value={inviteRole} onValueChange={(value: 'ae' | 'manager') => setInviteRole(value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ae">Account Executive (AE)</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setInviteDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleInviteUser}
                    disabled={inviteUserMutation.isPending}
                    className="bg-[#4AE2C9] hover:bg-[#3BC9B3] text-black"
                  >
                    {inviteUserMutation.isPending ? 'Inviting...' : 'Send Invite'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {(projectUsers as ProjectUser[]).length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No users assigned to this project yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(projectUsers as ProjectUser[]).map((user: ProjectUser) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center space-x-2">
                        {getRoleIcon(user.role)}
                        <span>{user.username}</span>
                        {isCurrentUser(user) && (
                          <Badge variant="outline" className="ml-2">You</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {canModifyUser(user) && user.role !== 'super_admin' ? (
                        <Select
                          value={user.role}
                          onValueChange={(value) => handleRoleChange(user.user_id, value)}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ae">Account Executive</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant={getRoleBadgeVariant(user.role)}>
                          {user.role === 'super_admin' ? 'Super Admin' : 
                           user.role === 'manager' ? 'Manager' : 'Account Executive'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {canModifyUser(user) && user.role !== 'super_admin' && !isCurrentUser(user) ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove User</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to remove {user.username} from this project? 
                                This action cannot be undone and they will lose access to all project data.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRemoveUser(user.user_id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Remove User
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Role Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Role Permissions</CardTitle>
          <CardDescription>
            Understand what each role can do in this project
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Crown className="h-5 w-5 text-yellow-500 mt-0.5" />
              <div>
                <h4 className="font-semibold">Super Admin</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Full access to all project features, user management, and system administration. Cannot be removed or downgraded.
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-semibold">Manager</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Can manage project users, oversee all modules, and handle exports. Can invite and manage Account Executives.
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex items-start space-x-3">
              <User className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-semibold">Account Executive (AE)</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Can participate in assessments, view results, and contribute to project modules. Cannot manage other users.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}